import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Link, Sparkles } from 'lucide-react';
import { useParams } from 'react-router-dom';

import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import { ToggleStage } from '@/components/Stage/ToggleStage';
import { JobStatus } from '@/features/Setting/Integrations/components/JobStatus/JobStatus';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services';

import type { ScrapingJob } from '../api';
import { ScrapingJobDetailContent } from '../components/ScrapingJobDetailContent/ScrapingJobDetailContent';
import { useScrapingJobDetailAction } from '../hooks/useScrapingJobDetailAction';
import {
  isCancelableScrapingJob,
  isDeletableAllFeedbackScrapingJob,
  isDeletableScrapingJob,
} from '../models/ScrapingJob';
import {
  SCRAPING_JOB_STATUS,
  ScrapingJobStatusToDisplayStatus,
} from '../types';
import { copyScrapingJobLink } from '../utils/copyScrapingJobLink';

export const ScrapingDetailPage = () => {
  const colors = useColor();
  const { jobId } = useParams();
  const wr = useWorkspaceRouter();

  if (!jobId) throw new Error('JobId is required');

  const {
    data: scrapingJob,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['scraping-detail', jobId],
    queryFn: () => service.scraping.retrieveScrapingJob(jobId),
  });

  return (
    <div className="bg-gray-200 h-full p-6 flex flex-col gap-4">
      <IconButton
        sizeType="l"
        icon={<ArrowLeft className="size-4 text-gray-700" />}
        onClick={() => wr.push('/settings/integration/scraping')}
        tooltip="Back to scraping list"
        outlined={false}
        style={{ backgroundColor: colors.gray100 }}
      />
      <ToggleStage
        status={!isLoading}
        data={scrapingJob}
        className="bg-gray-100 rounded-xl w-fit"
      >
        {(scrapingJob) => (
          <Content scrapingJob={scrapingJob} refetch={refetch} />
        )}
      </ToggleStage>
    </div>
  );
};

const Content = ({
  scrapingJob,
  refetch,
}: {
  scrapingJob: ScrapingJob;
  refetch: () => void;
}) => {
  const workspaceId = useWorkspaceId();
  const {
    loading,
    onDeleteScheduleClick,
    onCancelJobClick,
    onDeleteAllFeedbackClick,
    onConfirmClick,
    onSummarizeWithHeySynclyClick,
    heySynclyEnabled,
  } = useScrapingJobDetailAction(scrapingJob, {
    refetchCallback: refetch,
    onConfirmCallback: refetch,
  });
  return (
    <div className="flex flex-col gap-8 w-full p-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{scrapingJob.title}</h2>
        <div className="flex items-center gap-3">
          {heySynclyEnabled && (
            <IconButton
              tooltip="View insight with Hey syncly"
              sizeType="m"
              icon={<Sparkles className="size-4 text-gray-700" />}
              onClick={onSummarizeWithHeySynclyClick}
              outlined={false}
            />
          )}

          <IconButton
            icon={<Link className="size-4 text-gray-700" />}
            tooltip="Copy link"
            onClick={() => {
              copyScrapingJobLink(workspaceId, scrapingJob.alias);
            }}
            outlined={false}
          />

          <div className="flex items-center gap-1">
            <JobStatus
              status={ScrapingJobStatusToDisplayStatus.get(scrapingJob.status)}
            />
            {(scrapingJob.status === SCRAPING_JOB_STATUS.RUNNING_SCRAPING ||
              scrapingJob.status === SCRAPING_JOB_STATUS.CREATE_FEEDBACK ||
              scrapingJob.status === SCRAPING_JOB_STATUS.BLOCK_ANALYZING) && (
              <span className="text-xs font-normal text-gray-700">
                :{' '}
                {scrapingJob.status === SCRAPING_JOB_STATUS.BLOCK_ANALYZING
                  ? 95
                  : scrapingJob.progress}
                %
              </span>
            )}
          </div>
        </div>
      </div>
      <ScrapingJobDetailContent scrapingJob={scrapingJob} />
      <div className="flex gap-2">
        {isDeletableScrapingJob(scrapingJob) && (
          <Button
            variant="error_outlined"
            onClick={onDeleteScheduleClick}
            sizeType="m"
          >
            Delete schedule
          </Button>
        )}

        {isCancelableScrapingJob(scrapingJob) && (
          <Button
            variant="error_outlined"
            onClick={onCancelJobClick}
            sizeType="m"
          >
            Cancel job
          </Button>
        )}
        {isDeletableAllFeedbackScrapingJob(scrapingJob) && (
          <Button
            variant="error_outlined"
            onClick={onDeleteAllFeedbackClick}
            sizeType="m"
          >
            Delete all feedback
          </Button>
        )}
        {scrapingJob?.status === SCRAPING_JOB_STATUS.BEFORE_CONFIRM && (
          <Button
            variant="primary"
            onClick={onConfirmClick}
            sizeType="m"
            isLoading={loading}
          >
            Approve
          </Button>
        )}
      </div>
    </div>
  );
};
